/**
 * Library tab — workout templates (saved session structures).
 *
 * Exercises now live on the dedicated /health/exercises top-level page.
 * Selecting a template row opens WorkoutTemplateEditor. The "New template"
 * action is surfaced in the parent's header.
 */

import { useState } from 'react';
import { Icon } from '@k-os/ui';
import {
  useWorkoutTemplates,
  useStartFromTemplate,
  type ExerciseType,
  type WorkoutTemplateDto,
} from '../../../api/health';
import { EXERCISE_TYPE_META, TypeChip } from './atoms';
import styles from './workouts.module.css';

const TYPE_KEYS = Object.keys(EXERCISE_TYPE_META) as ExerciseType[];

export function WorkoutLibrary({
  onOpenTemplate,
  onStartSession,
  onNewTemplate,
}: {
  onOpenTemplate: (t: WorkoutTemplateDto) => void;
  onStartSession: (sessionId: string) => void;
  onNewTemplate: () => void;
}) {
  const templatesQ = useWorkoutTemplates();
  const start = useStartFromTemplate();

  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<ExerciseType | 'all'>('all');

  const templates = templatesQ.data?.templates ?? [];

  function handleStart(t: WorkoutTemplateDto): void {
    start.mutate(
      { id: t.id },
      { onSuccess: (res) => onStartSession(res.sessionId) },
    );
  }

  function matchesQuery(haystack: string): boolean {
    const lower = q.trim().toLowerCase();
    if (!lower) return true;
    return haystack.toLowerCase().includes(lower);
  }

  const filteredTemplates = templates.filter((t) => {
    if (filter !== 'all' && t.type !== filter) return false;
    return matchesQuery(`${t.name} ${t.description}`);
  });

  return (
    <div>
      {/* Search + New button */}
      <div
        style={{
          display: 'flex',
          gap: 10,
          marginBottom: 12,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search saved workouts…"
          className={styles.searchField}
          style={{ minWidth: 220 }}
        />
        <button type="button" className="kos-btn kos-btn-primary" onClick={onNewTemplate}>
          New template
        </button>
      </div>

      {/* Type filter chips */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        <button
          type="button"
          className={`${styles.filterChip} ${filter === 'all' ? styles.filterChipActive : ''}`}
          onClick={() => setFilter('all')}
        >
          All · {templates.length}
        </button>
        {TYPE_KEYS.map((t) => {
          const count = templates.filter((tp) => tp.type === t).length;
          if (count === 0) return null;
          const meta = EXERCISE_TYPE_META[t];
          return (
            <button
              key={t}
              type="button"
              className={`${styles.filterChip} ${filter === t ? styles.filterChipActive : ''}`}
              onClick={() => setFilter(t)}
            >
              <span
                style={{ width: 7, height: 7, borderRadius: 2, background: meta.color, marginRight: 4 }}
              />
              {meta.name} · {count}
            </button>
          );
        })}
      </div>

      {/* Body */}
      <TemplatesList
        loading={templatesQ.isLoading}
        rows={filteredTemplates}
        starting={start.isPending}
        empty={
          q || filter !== 'all'
            ? 'No templates match.'
            : 'No saved workout templates yet. Click "New template" to create one.'
        }
        onOpen={onOpenTemplate}
        onStart={handleStart}
      />
    </div>
  );
}

// ─── Workout-templates list (cards) ──────────────────────────────────────

function TemplatesList({
  loading,
  rows,
  starting,
  empty,
  onOpen,
  onStart,
}: {
  loading: boolean;
  rows: WorkoutTemplateDto[];
  starting: boolean;
  empty: string;
  onOpen: (t: WorkoutTemplateDto) => void;
  onStart: (t: WorkoutTemplateDto) => void;
}) {
  if (loading) {
    return <div style={{ color: 'var(--ink-3)' }}>Loading templates…</div>;
  }
  if (rows.length === 0) {
    return (
      <div
        style={{
          padding: 24,
          textAlign: 'center',
          color: 'var(--ink-3)',
          border: '1px dashed var(--line)',
          borderRadius: 8,
          background: 'var(--surface-2)',
        }}
      >
        {empty}
      </div>
    );
  }
  return (
    <div className={styles.libGrid}>
      {rows.map((t) => (
        <div key={t.id} className={styles.libCard}>
          <div className={styles.libCardHead}>
            <div className={styles.libCardName}>{t.name}</div>
            {isExerciseType(t.type) && <TypeChip type={t.type} />}
          </div>
          {t.description && <div className={styles.libCardDesc}>{t.description}</div>}
          <div className={styles.libCardMeta}>
            {t.durationMin && (
              <span>
                <strong>{t.durationMin}</strong> min
              </span>
            )}
            <span>
              <strong>{t.exercises.length}</strong> exercises
            </span>
            <span>
              used <strong>{t.usedCount}</strong>×
            </span>
            {t.agentBuilt && (
              <span style={{ marginLeft: 'auto', color: 'var(--accent)' }}>
                Agent-built
              </span>
            )}
          </div>
          <div className={styles.libCardActions}>
            <button
              type="button"
              className="kos-btn kos-btn-primary"
              style={{ flex: 1 }}
              onClick={() => onStart(t)}
              disabled={starting}
            >
              <Icon name="check" size={11} /> Start
            </button>
            <button type="button" className="kos-btn" onClick={() => onOpen(t)}>
              Edit
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function isExerciseType(v: string): v is ExerciseType {
  return TYPE_KEYS.includes(v as ExerciseType);
}
